import * as Sentry from '@sentry/react';
import { Form, FormikProvider, useFormik } from 'formik';
import React, { ReactElement, useState } from 'react';
import * as Yup from 'yup';
import {
  EMAIL_MAX_LEN,
  FLASH_ERROR_COOKIE_NAME,
  GROW_4028_TEST_REQ_WORK_EMAIL_DURING_SIGNUP,
  GROW_4028_TEST_REQ_WORK_EMAIL_DURING_SIGNUP_TREATMENT,
} from '../constants';
import copy from '../copy';
import { handlePlainFetchError } from '../utils/gql-utils';
import { yupPasswordValidation } from '../utils/validation-utils';
import ErrorBanner from './ErrorBanner';
import SpinnerButton from './SpinnerButton';
import TextInput from './TextInput';
import { registrationTrackingIds } from '../tracking-ids';
import { ButtonStyle } from './SharedStyles';
import { isAMD } from '../utils/checkLocationHost';
import { setCookieValue } from '@do/seashell';
import { useExperimentVariationAnon } from '@do/seashell/experimentation';

const formCopy = copy.form;

interface FormValues {
  name: string;
  password: string;
  email: string;
  onboarding_origin: string;
}

const FormValidation = Yup.object().shape({
  email: Yup.string()
    .email(formCopy.validation.email_valid)
    .required(formCopy.validation.email_required)
    .max(EMAIL_MAX_LEN, copy.errors.too_long.details),
  password: yupPasswordValidation,
});

interface Props {
  email?: string;
  onboardingOrigin?: string;
  sessionId?: string;
  isLoadingOauthPath?: boolean;
  buttonStyle?: ButtonStyle;
}

const RegistrationForm: React.FC<Props> = ({
  email,
  onboardingOrigin,
  isLoadingOauthPath,
  buttonStyle,
}): ReactElement => {
  const [genericServerError, setGenericServerError] = useState(null);
  const [isCreatingAccount, setIsCreatingAccount] = useState(false);
  const [sellingPointsVariant, loading] = useExperimentVariationAnon({
    experimentKey: GROW_4028_TEST_REQ_WORK_EMAIL_DURING_SIGNUP,
  });

  const showWorkEmailPlaceholderExperiment =
    sellingPointsVariant === GROW_4028_TEST_REQ_WORK_EMAIL_DURING_SIGNUP_TREATMENT;

  const formik = useFormik<FormValues>({
    initialValues: {
      name: '',
      password: '',
      email: email || '',
      onboarding_origin: onboardingOrigin || '',
    },
    validationSchema: FormValidation,
    onSubmit: async (values, { setErrors }) => {
      setIsCreatingAccount(true);
      setGenericServerError(null); // Reset displayed error

      try {
        const response = await fetch(`${window.location.origin}/api/v1/registrations/email`, {
          method: 'POST',
          headers: new Headers([['Content-Type', 'application/json']]),
          body: JSON.stringify(values),
          credentials: 'same-origin',
        });
        if (!response.ok) {
          Sentry.captureException(new Error(`Registration failed with status`));

          // If we are in AMD domain and the response error has code === 'email_exists', set the flash cookie with the response message
          // and redirect to the registration page to show the flash error;
          const apiErrors = await handlePlainFetchError(response, setErrors, setGenericServerError);
          const emailExistsError = apiErrors?.find((error) => error.code === 'email_exists');
          if (isAMD() && emailExistsError) {
            setCookieValue(FLASH_ERROR_COOKIE_NAME, JSON.stringify([emailExistsError]));
            window.location.assign('/registrations/new');
          }
        } else if (response.redirected) {
          window.location.assign(response.url);
        }
      } catch (error) {
        Sentry.captureException(error);
        console.error('Unexpected error during registration:', error);
      } finally {
        setIsCreatingAccount(false);
      }
    },
  });

  const { errors, values, touched } = formik;
  return (
    <FormikProvider value={formik}>
      <Form autoComplete="none">
        {genericServerError && <ErrorBanner error={genericServerError} />}

        <TextInput
          placeholder={formCopy.name_placeholder}
          name="name"
          label={formCopy.name_label}
          touched={touched.name}
          error={errors.name}
          value={values.name}
          autoComplete="none"
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
        />

        <TextInput
          placeholder={
            showWorkEmailPlaceholderExperiment ? formCopy.work_email_placeholder : formCopy.email_placeholder
          }
          required
          name="email"
          label={formCopy.email_label}
          touched={touched.email}
          error={errors.email}
          value={values.email}
          autoComplete="none"
        />

        <TextInput
          placeholder={formCopy.register.password_placeholder}
          required
          name="password"
          label={formCopy.password_label}
          type="password"
          touched={touched.password}
          error={errors.password}
          value={values.password}
          autoComplete="none"
        />
        <SpinnerButton
          buttonStyle={buttonStyle}
          type="submit"
          variation="success"
          fullWidth={true}
          disabled={isCreatingAccount || isLoadingOauthPath}
          spinning={isCreatingAccount || isLoadingOauthPath}
          loadingMessage={copy.loading.registration.belowButton}
          data-tracking-id={registrationTrackingIds.registrationForm.signUpButton}
        >
          {formCopy.register.button_text}
        </SpinnerButton>
      </Form>
    </FormikProvider>
  );
};

export default RegistrationForm;
